import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { Capabilities } from "@/auth/rbac";
import { EnvBadge } from "@/components/EnvBanner";
import { env } from "@/config/env";
import type { FeatureFlagDoc } from "@/firestore/featureFlags";
import {
  MAX_EXPECTED_BACK_DAYS,
  MAX_MESSAGE_LENGTH,
  MAX_REASON_LENGTH,
  clearsCopy,
  currentSnapshot,
  describeChange,
  describeTargeting,
  flagActionError,
  flagCommand,
  liveFlagFor,
  planVendorAction,
  resolveExpectedBackAt,
  superAdminHint,
  validMessage,
  validReason,
  vendorActionTitle,
  vendorList,
  vendorTarget,
  type VendorAction,
  type VendorPlanItem,
} from "./presentation";
import {
  FEATURE_FLAG_REGISTRY,
  VENDOR_LABELS,
  keysForVendor,
  type FeatureFlagVendor,
} from "./registry";
import type { FeatureFlagBatchItem, FeatureFlagBatchResult } from "./useFeatureFlagCommand";

export interface VendorActionDialogProps {
  vendor: FeatureFlagVendor;
  action: VendorAction;
  /** The live projection right now. */
  docs: readonly FeatureFlagDoc[];
  capabilities: Capabilities;
  pending: boolean;
  onRun(items: FeatureFlagBatchItem[]): Promise<FeatureFlagBatchResult[]>;
  onClose(): void;
}

const OUTCOME_LABELS = {
  write: { label: "Will change", color: "blue" },
  unchanged: { label: "Already there", color: "gray" },
  superAdminOnly: { label: "Skipped — superAdmin only", color: "yellow" },
  shared: { label: "Held", color: "orange" },
} as const;

function outcomeLabel(item: VendorPlanItem): string {
  return item.outcome === "shared"
    ? `Held — also ${vendorList(item.sharedWith)}`
    : OUTCOME_LABELS[item.outcome].label;
}

function stateText(doc: FeatureFlagDoc | null, defaultAvailable: boolean): string {
  const s = currentSnapshot(doc, defaultAvailable);
  const text = `iOS ${describeTargeting(s.iosMode, s.iosMinBuild, s.iosMaxBuild)} · Android ${describeTargeting(s.androidMode, s.androidMinBuild, s.androidMaxBuild)}`;
  return doc ? text : `${text} (code default)`;
}

/**
 * *Pause vendor X* / *Resume vendor X*: one reason and one confirmation for every flag tagged with
 * the vendor, written one at a time in registry order with a result per flag. Flags whose change
 * needs `superAdmin` are skipped, with a notice, for anyone else. A resume holds back flags another
 * vendor also tags until the operator confirms that vendor is not in maintenance.
 */
export function VendorActionDialog(props: VendorActionDialogProps) {
  const { vendor, action, capabilities } = props;
  const title = vendorActionTitle(action, vendor);
  // Snapshot what the operator reviewed; any live change to these keys locks the dialog.
  const [baseline] = useState(() => props.docs);
  const [message, setMessage] = useState("");
  const [backAtInput, setBackAtInput] = useState("");
  const [reason, setReason] = useState("");
  const [includeShared, setIncludeShared] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<FeatureFlagBatchResult[] | null>(null);

  const now = Date.now();
  const busy = props.pending || submitting;
  const locked = busy || confirming || results !== null;
  const pausing = action === "pause";
  const backAt = pausing
    ? resolveExpectedBackAt(backAtInput, now)
    : ({ kind: "value", value: null } as const);
  const messageOk = validMessage(message);
  const reasonOk = validReason(reason);
  const target = vendorTarget(action, {
    message: message.trim() || null,
    expectedBackAt: backAt.kind === "value" ? backAt.value : null,
  });
  const plan = planVendorAction(vendor, target, baseline, capabilities.superAdmin, includeShared);
  // The flags the confirmation below releases (computed without it, so ticking it keeps them listed).
  const shared = planVendorAction(vendor, target, baseline, capabilities.superAdmin).filter(
    (item) => item.outcome === "shared",
  );
  const sharedKeys = shared.map((item) => item.key).join(", ");
  const sharedVendors = vendorList([...new Set(shared.flatMap((item) => item.sharedWith))]);
  const writes = plan.filter((item) => item.outcome === "write");
  // A pause writes one message / expected-back time to every flag: left blank, it blanks the copy
  // a flag that is already off shows users. Resume clears it by design (and says so).
  const clearing = pausing
    ? writes.filter((item) => clearsCopy(item.before, target)).map((item) => item.key)
    : [];
  const skipped = plan.filter((item) => item.outcome === "superAdminOnly").map((item) => item.key);
  const stale =
    results === null &&
    keysForVendor(vendor).some(
      (key) =>
        (liveFlagFor(props.docs, key)?.updatedAt ?? null) !==
        (liveFlagFor(baseline, key)?.updatedAt ?? null),
    );

  const disabledReason = !capabilities.act
    ? "Your role can't change feature flags."
    : env.isProd && !capabilities.actProd
      ? "Your role can't act in production."
      : writes.length === 0
        ? "Nothing to change."
        : null;
  const valid = messageOk && reasonOk && backAt.kind !== "invalid";
  const canSubmit = disabledReason === null && valid && !stale && !busy && results === null;

  async function submit() {
    if (!canSubmit) return;
    const items = writes.map((item) => ({
      key: item.key,
      command: flagCommand(item.key, item.current, target, reason.trim()),
    }));
    setSubmitting(true);
    setError(null);
    try {
      setResults(await props.onRun(items));
    } catch (e) {
      setError(flagActionError(e));
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  }

  const submitLabel = `${title} (${writes.length} flag${writes.length === 1 ? "" : "s"})`;
  const submitButton = (
    <Button type="submit" color={pausing ? "red" : "teal"} disabled={!canSubmit} loading={busy}>
      {submitLabel}
    </Button>
  );

  return (
    <Modal
      opened
      title={title}
      onClose={() => {
        if (!busy) props.onClose();
      }}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      withCloseButton={!busy}
      size="xl"
      centered
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // Production: the first submit only opens the confirm step.
          if (env.isProd && !confirming) {
            if (canSubmit) setConfirming(true);
            return;
          }
          void submit();
        }}
      >
        <Stack>
          {env.isProd ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Group gap="xs">
                You are changing every {VENDOR_LABELS[vendor]} flag in <EnvBadge size="sm" />. Every
                device and the API follow within seconds.
              </Group>
            </Alert>
          ) : null}
          <Text size="sm" c="dimmed">
            {pausing
              ? "Switches every flag tagged with this vendor off on both platforms, with one message and one reason."
              : "Switches every flag tagged with this vendor back on for every build on both platforms and clears the message and the expected-back time."}
          </Text>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Flag</Table.Th>
                <Table.Th>Now</Table.Th>
                <Table.Th>{results ? "Result" : "Outcome"}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {plan.map((item) => {
                const result = results?.find((r) => r.key === item.key);
                return (
                  <Table.Tr key={item.key}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {item.key}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {stateText(item.current, FEATURE_FLAG_REGISTRY[item.key].defaultAvailable)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {result ? (
                        result.ok ? (
                          <Badge color="teal" variant="light" radius="sm" tt="none">
                            Saved
                          </Badge>
                        ) : (
                          <Stack gap={2}>
                            <Badge color="red" variant="light" radius="sm" tt="none">
                              Failed
                            </Badge>
                            <Text size="xs" c="red">
                              {result.error}
                            </Text>
                          </Stack>
                        )
                      ) : (
                        <Stack gap={2}>
                          <Badge
                            color={OUTCOME_LABELS[item.outcome].color}
                            variant="light"
                            radius="sm"
                            tt="none"
                          >
                            {outcomeLabel(item)}
                          </Badge>
                          {item.outcome === "write" ? (
                            <Text size="xs" c="dimmed">
                              {describeChange(item.key, item.before, target, item.current === null)}
                            </Text>
                          ) : null}
                        </Stack>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          {skipped.length > 0 ? (
            <Alert color="yellow" variant="light" title="Skipped — superAdmin only">
              <Stack gap={4}>
                {skipped.map((key) => (
                  <Text key={key} size="sm">
                    {superAdminHint(key, FEATURE_FLAG_REGISTRY[key].riskyDirection)}
                  </Text>
                ))}
                <Text size="sm">
                  Ask a superAdmin to change {skipped.length === 1 ? "it" : "them"}.
                </Text>
              </Stack>
            </Alert>
          ) : null}
          {shared.length > 0 ? (
            <Alert color="orange" variant="light" title="Shared with another vendor">
              <Stack gap="xs">
                <Text size="sm">
                  {sharedKeys} {shared.length === 1 ? "is" : "are"} also tagged {sharedVendors}.
                  Switching {shared.length === 1 ? "it" : "them"} back on ends a {sharedVendors}{" "}
                  pause too, so {shared.length === 1 ? "it is" : "they are"} held unless you
                  confirm.
                </Text>
                <Checkbox
                  label={`No ${sharedVendors} maintenance is under way — switch ${sharedKeys} back on too`}
                  checked={includeShared}
                  onChange={(event) => setIncludeShared(event.currentTarget.checked)}
                  disabled={locked}
                />
              </Stack>
            </Alert>
          ) : null}
          {pausing ? (
            <>
              <Textarea
                label="Message shown to users (optional)"
                description={`Public — anyone can read it, even before sign-in. Plain text, written to every paused flag. ${message.trim().length}/${MAX_MESSAGE_LENGTH}`}
                value={message}
                onChange={(event) => setMessage(event.currentTarget.value)}
                disabled={locked}
                minRows={2}
                autosize
                error={messageOk ? undefined : `At most ${MAX_MESSAGE_LENGTH} characters.`}
              />
              <TextInput
                type="datetime-local"
                label="Expected back (optional)"
                description={`Your local time, at most ${MAX_EXPECTED_BACK_DAYS} days ahead. Display only — nothing switches back on automatically.`}
                value={backAtInput}
                onChange={(event) => setBackAtInput(event.currentTarget.value)}
                disabled={locked}
                error={backAt.kind === "invalid" ? backAt.reason : undefined}
              />
              {clearing.length > 0 ? (
                <Alert color="orange" variant="light" title="Existing copy will be cleared">
                  {clearing.join(", ")} already {clearing.length === 1 ? "shows" : "show"} users a
                  message or an expected-back time. A blank field above clears it on{" "}
                  {clearing.length === 1 ? "that flag" : "those flags"}; fill it in to replace it
                  instead.
                </Alert>
              ) : null}
            </>
          ) : null}
          <Textarea
            label="Reason (required)"
            description={`Recorded for audit on every flag written, against your operator account; never shown to users. ${reason.trim().length}/${MAX_REASON_LENGTH}`}
            placeholder={
              pausing ? "e.g. Rain maintenance window, ticket #…" : "e.g. Rain all-clear"
            }
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            disabled={locked}
            minRows={2}
            autosize
            required
            error={
              reason.length > 0 && !reasonOk ? `1 to ${MAX_REASON_LENGTH} characters.` : undefined
            }
          />
          {stale ? (
            <Alert color="orange">
              One of these flags changed while you were reviewing. Close the dialog, review the
              latest values, and reopen it.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red">
              <Text size="sm">{error}</Text>
            </Alert>
          ) : null}
          <Text size="xs" c="dimmed">
            Flags are written one at a time, in the order listed; a failure is reported and the rest
            carry on. Devices and the API follow within seconds.
          </Text>
          {results ? (
            <Group justify="flex-end">
              <Button onClick={props.onClose}>Close</Button>
            </Group>
          ) : confirming ? (
            <Alert color="red" variant="light" title="Confirm the production change">
              <Stack gap="xs">
                <Text size="sm">
                  {title}: {writes.map((item) => item.key).join(", ")}. Real users see this within
                  seconds.
                </Text>
                <Group justify="flex-end">
                  <Button variant="default" disabled={busy} onClick={() => setConfirming(false)}>
                    Back
                  </Button>
                  <Button type="submit" color="red" disabled={!canSubmit} loading={busy}>
                    Confirm and save
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : (
            <Group justify="flex-end">
              <Button variant="default" disabled={busy} onClick={props.onClose}>
                Cancel
              </Button>
              {disabledReason ? (
                <Tooltip label={disabledReason} multiline w={320} withArrow>
                  <span>{submitButton}</span>
                </Tooltip>
              ) : (
                submitButton
              )}
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
